import {
  Column,
  ColumnKeys,
  DNSRecordType,
  EasmArtifactsInventoryGridViewData,
  EASMCountryType,
  SMART_VIEW_MAX_INPUT_LENGTH,
} from '../types';
import { FindingType } from '../../types';
import { ColumnDetails, ColumnType, ColumnValue } from '../../../grid/types/common';
import { translateFn } from '../../../utils/translator.helper';
import { CategoriesConfig } from '../../../grid/genericGridSmartViews/types';
import { EnumUtils } from '../../../utils/enum.utils';
import { SmartViewInlineActions } from '../../../grid/gridSmartViews/types';
import { RemoteDetailsTypes } from '../../../filters/backendEnumFilter/types';
import { ExpirationStatus } from '../../types';
import {
  EasmInventoryBaseViewDataProvider,
} from '../../assetsInventoryGrid/dataProvider/EasmInventoryBaseViewDataProvider';

export class EasmArtifactsInventoryGridViewDataProvider extends EasmInventoryBaseViewDataProvider {
  public async getDataForView(): Promise<EasmArtifactsInventoryGridViewData> {
    const investigationStatusOptions = this.getInvestigationStatusOptions(),
      priorityOptions = this.getPriorityOptions(),
      predefinedFilterKeys = [
        ColumnKeys.NAME,
        ColumnKeys.TYPE,
        ColumnKeys.INVESTIGATION_STATUS,
        ColumnKeys.ASSIGNEE,
        ColumnKeys.PRIORITY,
        ColumnKeys.ASSETS_RELATED_TO_ARTIFACT,
      ];

    if (this.dependencies.permissions.canHaveChildCompanies) {
      predefinedFilterKeys.unshift(ColumnKeys.COMPANY);
    }

    return {
      smartViewsCategories: this.getSmartViewsCategories(),
      allColumns: await this.getAllColumns(investigationStatusOptions, priorityOptions),
      predefinedFilterKeys: predefinedFilterKeys,
      investigationStatusOptions,
      assigneeOptions: await this.getAssigneeOptions(),
      priorityOptions,
    };
  }

  private getSmartViewsCategories(): CategoriesConfig {
    return {
      internalCategories: {
        recent: {
          isAvailable: false,
        },
        default: {
          isAvailable: true,
          translations: {
            all_artifacts: translateFn('All artifacts'),
            asn_reports: translateFn('ASN information'),
            certificate_reports: translateFn('Certificates'),
            ip_blocks: translateFn('IP blocks'),
            dns_records: translateFn('DNS records'),
            services: translateFn('Services'),
            artifacts_assigned_to_you: translateFn('Artifacts assigned to you'),
            newest_artifacts: translateFn('New artifacts'),
          },
        },
        favorites: {
          isAvailable: true,
        },
      },
      customCategories: [
        {
          sectionTitle: this.dependencies.translateFn('Saved'),
          emptyStateItemText: this.dependencies.translateFn('No queries added'),
          type: 'saved',
          inlineActions: [
            {
              title: this.dependencies.translateFn('Rename'),
              value: SmartViewInlineActions.RENAME,
            },
            {
              title: this.dependencies.translateFn('Delete'),
              value: SmartViewInlineActions.DELETE,
              customClass: 'danger',
            },
          ],
          isOpen: false,
          smartViewMaxInputLength: SMART_VIEW_MAX_INPUT_LENGTH,
          noSpaceEscape: true,
          smartViewValidators: [],
          items: [],
        },
      ],
    };
  }

  private async getAllColumns(
    investigationStatusOptions: ColumnValue[],
    priorityOptions: ColumnValue[]
  ): Promise<Array<ColumnDetails<Column, ColumnKeys>>> {
    const columns: any = [
      {
        column: Column.NAME,
        key: ColumnKeys.NAME,
        columnName: translateFn('Artifact name'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        remoteDetails: {
          type: RemoteDetailsTypes.EASMArtifactsName,
        },
        removable: false,
        sortable: false,
        category: translateFn('Common columns'),
        width: '240px',
      },
      {
        column: Column.TYPE,
        key: ColumnKeys.TYPE,
        columnName: translateFn('Artifact type'),
        type: ColumnType.ENUM,
        values: EnumUtils.GetNumericValues<FindingType>([
          FindingType.ASN_REPORT,
          FindingType.CERTIFICATE_REPORT,
          FindingType.IP_BLOCK,
          FindingType.DNS_RECORD,
          FindingType.SERVICE,
        ]).map(
          (type: FindingType): ColumnValue => ({
            label: this.stringsTranslator.translateEASMType(type),
            value: type,
          })
        ),
        removable: false,
        sortable: false,
        filterResetValue: [],
        category: translateFn('Common columns'),
      },
      {
        column: Column.INVESTIGATION_STATUS,
        key: ColumnKeys.INVESTIGATION_STATUS,
        columnName: translateFn('Investigation status'),
        type: ColumnType.ENUM,
        values: investigationStatusOptions,
        removable: false,
        filterResetValue: [],
        sortable: false,
        category: translateFn('Common columns'),
      },
      {
        column: Column.ASSETS_RELATED_TO_ARTIFACT,
        key: ColumnKeys.ASSETS_RELATED_TO_ARTIFACT,
        columnName: translateFn('Related assets'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        removable: false,
        sortable: false,
        remoteDetails: {
          type: RemoteDetailsTypes.EASMArtifactsRelatedAssets,
        },
        editingDisabled: true,
        category: translateFn('Common columns'),
      },
      {
        column: Column.NOTES,
        key: ColumnKeys.NOTES,
        columnName: translateFn('Notes'),
        type: ColumnType.ENUM,
        values: [
          {
            label: translateFn('Yes'),
            value: 1,
          },
          {
            label: translateFn('No'),
            value: 0,
          },
        ],
        removable: true,
        sortable: false,
        category: translateFn('Common columns'),
      },
      {
        column: Column.ASSIGNEE,
        key: ColumnKeys.ASSIGNEE,
        columnName: translateFn('Assignee'),
        type: ColumnType.SELECT,
        values: await this.getAssigneeFilterOptions(),
        removable: false,
        sortable: false,
        category: translateFn('Common columns'),
      },
      {
        column: Column.PRIORITY,
        key: ColumnKeys.PRIORITY,
        columnName: translateFn('Priority'),
        type: ColumnType.ENUM,
        values: priorityOptions,
        removable: false,
        filterResetValue: [],
        sortable: false,
        category: translateFn('Common columns'),
      },
      {
        column: Column.COUNTRY,
        key: ColumnKeys.COUNTRY,
        columnName: translateFn('ASN country'),
        type: ColumnType.ENUM,
        values: EnumUtils.GetValues<EASMCountryType>(EASMCountryType).map(
          (type: EASMCountryType): ColumnValue => ({
            label: this.stringsTranslator.translateEASMCountryType(type),
            value: type,
          })
        ),
        removable: true,
        sortable: false,
        category: translateFn('ASN information'),
      },
      {
        column: Column.OPERATOR,
        key: ColumnKeys.OPERATOR,
        columnName: translateFn('ASN operator'),
        type: ColumnType.STRING,
        removable: true,
        sortable: false,
        category: translateFn('ASN information'),
      },
      {
        column: Column.CERTIFICATE_REPORTS_ID,
        key: ColumnKeys.CERTIFICATE_REPORTS_ID,
        columnName: translateFn('Certificate ID'),
        type: ColumnType.STRING,
        removable: true,
        sortable: false,
        category: translateFn('Certificates'),
      },
      {
        column: Column.CERTIFICATE_REPORTS_ISSUER_NAME,
        key: ColumnKeys.CERTIFICATE_REPORTS_ISSUER_NAME,
        columnName: translateFn('Issuer name'),
        type: ColumnType.STRING,
        removable: true,
        sortable: false,
        category: translateFn('Certificates'),
      },
      {
        column: Column.CERTIFICATE_REPORTS_EXPIRATION_STATUS,
        key: ColumnKeys.CERTIFICATE_REPORTS_EXPIRATION_STATUS,
        columnName: translateFn('Certificate expiration status'),
        type: ColumnType.ENUM,
        values: EnumUtils.GetNumericValues<ExpirationStatus>(ExpirationStatus).map(
          (status: ExpirationStatus): ColumnValue => ({
            label: this.stringsTranslator.translateExpirationStatus(status),
            value: status,
          })
        ),
        filterResetValue: [],
        removable: true,
        sortable: false,
        category: translateFn('Certificates'),
      },
      {
        column: Column.CERTIFICATE_REPORTS_VALID_FROM,
        key: ColumnKeys.CERTIFICATE_REPORTS_VALID_FROM,
        columnName: translateFn('Valid from'),
        type: ColumnType.DATE,
        removable: true,
        sortable: true,
        filteringDisabled: true,
        category: translateFn('Certificates'),
      },
      {
        column: Column.CERTIFICATE_REPORTS_VALID_UNTIL,
        key: ColumnKeys.CERTIFICATE_REPORTS_VALID_UNTIL,
        columnName: translateFn('Valid until'),
        type: ColumnType.DATE,
        removable: true,
        sortable: true,
        filteringDisabled: true,
        category: translateFn('Certificates'),
      },
      {
        column: Column.CERTIFICATE_REPORTS_SERIAL_NUMBER,
        key: ColumnKeys.CERTIFICATE_REPORTS_SERIAL_NUMBER,
        columnName: translateFn('Serial number'),
        type: ColumnType.STRING,
        removable: true,
        sortable: false,
        category: translateFn('Certificates'),
      },
      {
        column: Column.DNS_RECORDS_TYPE,
        key: ColumnKeys.DNS_RECORDS_TYPE,
        columnName: translateFn('DNS record type'),
        type: ColumnType.ENUM,
        values: EnumUtils.GetValues<DNSRecordType>(DNSRecordType).map(
          (type: DNSRecordType): ColumnValue => ({
            label: this.stringsTranslator.translateEASMDNSRecordType(type),
            value: type,
          })
        ),
        removable: true,
        sortable: false,
        category: translateFn('DNS records'),
      },
      {
        column: Column.DNS_RECORDS_MS,
        key: ColumnKeys.DNS_RECORDS_MS,
        columnName: translateFn('Mail server'),
        type: ColumnType.STRING,
        removable: true,
        sortable: false,
        category: translateFn('DNS records'),
      },
      {
        column: Column.DNS_RECORDS_NS,
        key: ColumnKeys.DNS_RECORDS_NS,
        columnName: translateFn('Name server'),
        type: ColumnType.STRING,
        removable: true,
        sortable: false,
        category: translateFn('DNS records'),
      },
      {
        column: Column.DNS_RECORDS_CNAME,
        key: ColumnKeys.DNS_RECORDS_CNAME,
        columnName: translateFn('CNAME'),
        type: ColumnType.STRING,
        removable: true,
        sortable: false,
        category: translateFn('DNS records'),
      },
      {
        column: Column.VULNERABLE_TO_ZONE_TRANSFER,
        key: ColumnKeys.VULNERABLE_TO_ZONE_TRANSFER,
        columnName: translateFn('Vulnerable to zone transfer'),
        type: ColumnType.ENUM,
        values: [
          {
            label: translateFn('Yes'),
            value: 1,
          },
          {
            label: translateFn('No'),
            value: 0,
          },
        ],
        removable: true,
        sortable: false,
        category: translateFn('DNS records'),
      },
      {
        column: Column.NEW_ARTIFACTS,
        key: ColumnKeys.NEW_ARTIFACTS,
        columnName: translateFn('Show only new artifacts'),
        type: ColumnType.ENUM,
        values: [
          {
            label: translateFn('Yes'),
            value: 1,
          },
          {
            label: translateFn('No'),
            value: 0,
          },
        ],
        removable: true,
        sortable: false,
        category: translateFn('New artifacts'),
        isHiddenInGridSettings: true,
      },
      {
        column: Column.DNS_RESOLUTION_STATUS,
        key: ColumnKeys.DNS_RESOLUTION_STATUS,
        columnName: translateFn('DNS resolution status'),
        type: ColumnType.ENUM,
        values: [
          {
            label: 'NOERROR',
            value: 'NOERROR',
          },
          {
            label: 'SERVFAIL',
            value: 'SERVFAIL',
          },
        ],
        removable: true,
        sortable: false,
        category: translateFn('DNS records'),
      },
      {
        column: Column.SERVICES_PORT,
        key: ColumnKeys.SERVICES_PORT,
        columnName: translateFn('Port'),
        type: ColumnType.NUMBER,
        removable: true,
        sortable: false,
        category: translateFn('Services'),
      },
      {
        column: Column.SERVICES_PROTOCOL,
        key: ColumnKeys.SERVICES_PROTOCOL,
        columnName: translateFn('Protocol'),
        type: ColumnType.STRING,
        removable: true,
        sortable: false,
        category: translateFn('Services'),
      },
      {
        column: Column.SERVICES_CVES,
        key: ColumnKeys.SERVICES_CVES,
        columnName: translateFn('CVEs'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        removable: true,
        sortable: false,
        remoteDetails: {
          type: RemoteDetailsTypes.EASMArtifactsCVEs,
        },
        category: translateFn('Services'),
      },
      {
        column: Column.CHANGE_PROPERTY_ACTIONS,
        key: ColumnKeys.CHANGE_PROPERTY_ACTIONS,
        columnName: '',
        type: ColumnType.ACTION,
        removable: false,
        category: translateFn('Common columns'),
      },
    ];
    if (this.dependencies.permissions.canHaveChildCompanies) {
      columns.unshift({
        column: Column.COMPANY,
        key: ColumnKeys.COMPANY,
        columnName: translateFn('Company'),
        type: ColumnType.REMOTE_SELECT,
        removable: false,
        sortable: false,
        filterResetValue: {
          exactMatch: (Application.Main as any).user.company.id,
        },
        category: translateFn('Common columns'),
      });
    }
    return columns;
  }
}
